import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useIntl } from "react-intl";
import moment from "moment";
import { Button, Input, Modal, Table, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { carpetByRef, checkOutEntries, searchEntries } from "../../redux/actions";
import CleanStatus from "../CarpetPreview/CleanStatus";
import ClientSelect from "./ClientSelect";
import { clientName, dimensionsText, money } from "./format";

const { Title } = Typography;

export default function CheckOut() {
  const intl = useIntl();
  const history = useHistory();
  const dispatch = useDispatch();
  const [ref, setRef] = useState("");
  const [lines, setLines] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advancedClient, setAdvancedClient] = useState(undefined);
  const [advancedRows, setAdvancedRows] = useState([]);
  const [advancedTotal, setAdvancedTotal] = useState(0);
  const [advancedPage, setAdvancedPage] = useState(1);
  const [advancedLoading, setAdvancedLoading] = useState(false);
  const [advancedSelected, setAdvancedSelected] = useState({});

  const add = () => {
    if (!ref.trim()) return;
    dispatch(carpetByRef(ref.trim()))
      .then(({ payload }) => {
        if (!payload.entry) {
          setError("notInShop");
          return;
        }
        if (lines.some((line) => line.carpet._id === payload.carpet._id)) {
          setError("duplicateRef");
          return;
        }
        setLines((current) => current.concat({ carpet: payload.carpet, entry: payload.entry, outNotes: "" }));
        setRef("");
        setError(null);
      })
      .catch(() => setError("notInShop"));
  };

  const loadAdvanced = (clientId, page = 1) => {
    if (!clientId) {
      setAdvancedRows([]);
      setAdvancedTotal(0);
      setAdvancedPage(1);
      return;
    }
    setAdvancedLoading(true);
    dispatch(searchEntries(page, 10, { status: "in", client: clientId }))
      .then(({ payload }) => {
        setAdvancedRows(payload.data || []);
        setAdvancedTotal(payload.total || 0);
        setAdvancedPage(page);
      })
      .finally(() => setAdvancedLoading(false));
  };

  const addEntries = (entries) => {
    setLines((current) => {
      const existing = new Set(current.map((line) => String(line.carpet._id)));
      const added = entries
        .filter((entry) => entry.carpet && !existing.has(String(entry.carpet._id)))
        .map((entry) => ({
          carpet: { ...entry.carpet, client: entry.client || entry.carpet.client },
          entry,
          outNotes: "",
        }));
      return current.concat(added);
    });
  };

  const addAdvanced = () => {
    addEntries(Object.values(advancedSelected));
    setAdvancedOpen(false);
    setAdvancedSelected({});
  };

  const submit = () => {
    if (!lines.length) return;
    setSaving(true);
    dispatch(checkOutEntries(lines.map((line) => ({ carpetId: line.carpet._id, outNotes: line.outNotes }))))
      .then(({ payload }) => history.push({ pathname: "/carpets/report", state: { entries: payload, sheet: "checkOut" } }))
      .catch((err) => setError((err && err.message) || "notInShop"))
      .finally(() => setSaving(false));
  };

  return (
    <div>
      <Title level={4} style={{ marginTop: 0 }}>{intl.formatMessage({ id: "checkOut" })}</Title>
      <div className="no-print" style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <Input.Search
          value={ref}
          placeholder={intl.formatMessage({ id: "ref" })}
          style={{ width: 180 }}
          enterButton={<SearchOutlined />}
          onChange={(event) => setRef(event.target.value)}
          onSearch={add}
        />
        <Button onClick={() => { setAdvancedSelected({}); setAdvancedOpen(true); }}>
          {intl.formatMessage({ id: "advancedSearch" })}
        </Button>
        {error && <span style={{ color: "#cf1322", lineHeight: "32px" }}>{intl.formatMessage({ id: error })}</span>}
        <Button type="primary" style={{ marginLeft: "auto" }} disabled={!lines.length} loading={saving} onClick={submit}>
          {intl.formatMessage({ id: "checkOut" })}
        </Button>
      </div>
      <Table
        size="small"
        bordered
        pagination={false}
        scroll={{ x: "max-content" }}
        rowKey={(line) => line.carpet._id}
        dataSource={lines}
        columns={[
          { title: intl.formatMessage({ id: "ref" }), width: 80, render: (line) => line.carpet._ref },
          { title: intl.formatMessage({ id: "client" }), width: 180, render: (line) => clientName(line.carpet.client) },
          { title: intl.formatMessage({ id: "category" }), width: 120, render: (line) => intl.formatMessage({ id: line.carpet.category }) },
          {
            title: intl.formatMessage({ id: "price" }),
            width: 150,
            onHeaderCell: () => ({ style: { textAlign: "center" } }),
            render: (line) => (
              <div style={{ display: "flex", margin: "-8px" }}>
                <div style={{ flex: 1, padding: "8px", borderRight: "1px solid #f0f0f0", textDecoration: line.entry.jobPrice != null && line.entry.jobPrice !== "" ? "line-through" : undefined }}>{money(line.carpet.price)}</div>
                <div style={{ flex: 1, padding: "8px" }}>{money(line.entry.jobPrice)}</div>
              </div>
            ),
          },
          { title: intl.formatMessage({ id: "widthXLength" }), width: 150, render: (line) => dimensionsText(line.carpet) },
          { title: intl.formatMessage({ id: "storageBox" }), width: 90, render: (line) => line.entry.storageBox || "" },
          { title: intl.formatMessage({ id: "cleanStatus" }), width: 110, render: (line) => <CleanStatus cleaned={line.entry.cleaned} /> },
          { title: intl.formatMessage({ id: "inAt" }), width: 140, render: (line) => (line.entry.inAt ? moment(line.entry.inAt).format("DD/MM/YYYY HH:mm") : "") },
          { title: intl.formatMessage({ id: "inNotes" }), render: (line) => line.entry.inNotes || "" },
          {
            title: intl.formatMessage({ id: "outNotes" }),
            width: 220,
            render: (line) => (
              <Input
                size="small"
                value={line.outNotes}
                onChange={(event) => {
                  const value = event.target.value;
                  setLines((current) => current.map((item) => (item.carpet._id === line.carpet._id ? { ...item, outNotes: value } : item)));
                }}
              />
            ),
          },
          {
            width: 90,
            render: (line) => (
              <Button size="small" onClick={() => setLines((current) => current.filter((item) => item.carpet._id !== line.carpet._id))}>
                {intl.formatMessage({ id: "remove" })}
              </Button>
            ),
          },
        ]}
      />
      <Modal
        title={intl.formatMessage({ id: "advancedSearch" })}
        visible={advancedOpen}
        width={860}
        okText={intl.formatMessage({ id: "add" })}
        cancelText={intl.formatMessage({ id: "cancel" })}
        okButtonProps={{ disabled: !Object.keys(advancedSelected).length }}
        onOk={addAdvanced}
        onCancel={() => setAdvancedOpen(false)}
        destroyOnClose
      >
        <ClientSelect
          value={advancedClient}
          style={{ width: 280, marginBottom: 12 }}
          getPopupContainer={(node) => node.parentNode}
          onChange={(clientId) => {
            setAdvancedClient(clientId);
            setAdvancedSelected({});
            loadAdvanced(clientId, 1);
          }}
        />
        {!advancedClient && (
          <div style={{ marginBottom: 8 }}>{intl.formatMessage({ id: "clientRequiredDescription" })}</div>
        )}
        <Table
          size="small"
          bordered
          rowKey="_id"
          loading={advancedLoading}
          dataSource={advancedRows}
          pagination={{ current: advancedPage, pageSize: 10, total: advancedTotal }}
          onChange={(pagination) => loadAdvanced(advancedClient, pagination.current)}
          rowSelection={{
            selectedRowKeys: Object.keys(advancedSelected),
            preserveSelectedRowKeys: true,
            onChange: (keys) => {
              setAdvancedSelected((current) => {
                const pageRows = new Map(advancedRows.map((row) => [String(row._id), row]));
                const next = {};
                keys.forEach((key) => {
                  const record = pageRows.get(String(key)) || current[key] || current[String(key)];
                  if (record) next[String(key)] = record;
                });
                return next;
              });
            },
          }}
          columns={[
            { title: intl.formatMessage({ id: "ref" }), width: 80, render: (entry) => (entry.carpet || {})._ref },
            { title: intl.formatMessage({ id: "category" }), width: 120, render: (entry) => (entry.carpet && entry.carpet.category ? intl.formatMessage({ id: entry.carpet.category }) : "") },
            { title: intl.formatMessage({ id: "widthXLength" }), width: 150, render: (entry) => dimensionsText(entry.carpet) },
            { title: intl.formatMessage({ id: "storageBox" }), dataIndex: "storageBox", width: 90 },
            { title: intl.formatMessage({ id: "cleanStatus" }), width: 120, render: (entry) => <CleanStatus cleaned={entry.cleaned} /> },
            { title: intl.formatMessage({ id: "inAt" }), width: 150, render: (entry) => (entry.inAt ? moment(entry.inAt).format("DD/MM/YYYY HH:mm") : "") },
          ]}
        />
      </Modal>
    </div>
  );
}
